"use client";

import type { ReactNode } from "react";
import { PaywallProvider } from "@/context/PaywallContext";
import { SessionSync } from "@/components/auth/session-sync";
import { MobileSessionHandler } from "@/components/auth/mobile-session-handler";
import { DeepLinkHandler } from "@/components/deep-link-handler";
import { EarlyPushTapHandler } from "@/components/early-push-tap-handler";
import { PushNotificationDeepLinkHandler } from "@/components/push-notification-deeplink-handler";
import { PostHogIdentity } from "@/components/posthog-identity";
import { AuthGate } from "@/components/auth/auth-gate";

/**
 * Everything that only matters once there is an account behind the screen.
 *
 * WHY IT IS NOT IN THE ROOT LAYOUT ANY MORE. All of this used to mount on every page,
 * website included. Each piece reaches the auth store, which reaches the Supabase browser
 * client, so a visitor reading the cookie policy downloaded 230KB of authentication library
 * to sit idle. Measured 2026-08-25.
 *
 * WHAT DOES NOT CHANGE. Access control does not live here and never did. Middleware decides
 * who may open an app route, on the server, before any JavaScript runs: /dashboard,
 * /discover, /messages, /settings and /admin all answer 307 to /auth/login on a plain curl
 * with no browser involved. AuthGate below is a flash-preventer, not a lock, and it only
 * acts on "/" and the /auth screens (see auth-gate.tsx). Moving it cannot open a door.
 *
 * WHERE IT MOUNTS. Every route outside the website: the (app) and (auth) groups, the
 * homepage, /welcome, /r, /b, /invite, /scan and /v2, plus the website's own legal pages
 * WHEN they are being rendered inside the phone app, where a deep link or a push tap can
 * still arrive while somebody is reading the terms. Each of those mounts it directly rather
 * than through a lazy import, so nothing about the timing changes for them.
 *
 * If you add a route outside src/app/(site), it needs this.
 */
export function AppRuntime({ children }: { children: ReactNode }) {
  return (
    <PaywallProvider>
      <AppRuntimeEffects />
      <AuthGate>{children}</AuthGate>
    </PaywallProvider>
  );
}

/**
 * The listening half on its own: session refresh, deep links, push taps, analytics identity.
 * Every one of these renders null; they exist to watch for something happening.
 *
 * Separate from AppRuntime because the website's legal pages are also app screens, reached
 * from settings inside the phone app, and a push notification tapped while somebody is
 * reading the terms still has to open the right screen. Those pages take this on its own:
 * they have no use for the paywall context, and AuthGate does nothing on them anyway since
 * it only acts on "/" and the /auth screens.
 */
export function AppRuntimeEffects() {
  return (
    <>
      <SessionSync />
      <MobileSessionHandler />
      <DeepLinkHandler />
      <EarlyPushTapHandler />
      <PushNotificationDeepLinkHandler />
      <PostHogIdentity />
    </>
  );
}
